import {
  notificationVariants,
  notificationButtonVariants,
  closeButtonStyles,
} from './Notification.styles';
import { cn } from '@/utils';
import { Icon } from '@/components/Icons';
import { TNotificationVariant } from '@/types';
import React from 'react';

export interface NotificationAction {
  /**
   * The label text for the action button.
   */
  label: string;

  /**
   * The action to perform when the button is clicked.
   */
  onClick: () => void;

  /**
   * The visual variant of the action button.
   */
  variant?: 'primary' | 'secondary';
}

export interface NotificationProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The main message content of the notification.
   */
  message: string;

  /**
   * Array of action buttons to display in the notification.
   * Maximum of 2 actions recommended for optimal UX.
   */
  actions?: NotificationAction[];

  /**
   * Whether to show a close button in the top-right corner.
   * Default is true.
   */
  showCloseButton?: boolean;

  /**
   * Callback function called when the close button is clicked.
   */
  onClose?: () => void;

  /**
   * Notification variant which defines the visual style.
   * Default is 'default'.
   */
  variant?: TNotificationVariant;
}

export const Notification: React.FC<NotificationProps> = ({
  message,
  actions = [],
  showCloseButton = true,
  onClose,
  variant = 'default',
  className,
  ...props
}) => {
  /**
   * Combine all class names into a single string. This includes base styles,
   * variant styles, and any additional classes passed via props.
   */
  const classNames = cn(notificationVariants({ variant }), className);

  /**
   * Handle close button click
   */
  const handleClose = () => {
    onClose?.();
  };

  return (
    <div className={classNames} {...props}>
      <div className="flex flex-col gap-6">
        {/* Content area with message and close button */}
        <div className="flex w-full items-start align gap-3">
          {/* Message content */}
          <div className="min-w-0 flex-1">
            <p className="text-neutral-gray-800">{message}</p>
          </div>

          {/* Close button */}
          {showCloseButton && (
            <button
              type="button"
              className={closeButtonStyles}
              onClick={handleClose}
              aria-label="Close notification"
            >
              <Icon name="X" size={12} />
            </button>
          )}
        </div>

        {/* Action buttons */}
        {actions.length > 0 && (
          <div className="flex items-start gap-1">
            {actions.map((action, index) => (
              <button
                key={index}
                type="button"
                className={cn(notificationButtonVariants({ variant: action.variant || 'primary' }))}
                onClick={action.onClick}
              >
                {action.label}
              </button>
              // <Button
              //   key={index}
              //   variant={action.variant || 'primary'}
              //   size="small"
              //   onClick={action.onClick}
              //   label={action.label}
              // />
              // <button
              //   key={index}
              //   type="button"
              //   className={cn(notificationButtonVariants({ variant: action.variant || 'primary' }))}
              //   onClick={action.onClick}
              // >
              //   {action.label}
              // </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

Notification.displayName = 'Notification';

export default Notification;
